import React from "react";
import PropTypes from "prop-types";
// Material imports
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
//Limitless imports
import Grid from "@limitless/modern/layout/Grid";
import TextField from "@limitless/modern/inputs/text-field/TextField";
import Button from "@limitless/modern/inputs/button/Button";
import AnimationWrapper from "@limitless/modern/components/animation-wrapper/AnimationWrapper";
import Typography from "@limitless/modern/data-display/typography/Typography";
import DropdownMenu from "@limitless/modern/inputs/dropdown-menu/DropdownMenu";
import { useExpertProfileContext, INITIAL_FIELD } from "../../../../../expert";
import { findUserCountry, getUserDob } from "../../../../../expert/utils/AboutFormHelper";
import { useAppConfig, useNotificationContext } from "@limitless/common/hooks";
import { validateProfileForm } from "../../../../../expert/utils/validators/AboutFormValidator";
import { changePassword } from "../../../../../expert/apis/authentication-api/AuthenticationAPI";
import { getAllDirtyFields } from "../../../../../expert/utils/FieldValidators";
import SuccessAnimation from "@limitless/modern/components/success-animation/SuccessAnimation";
import { useTranslation } from "@limitless/common/hooks";
import CalendarIcon from "@limitless/icons/Calendar";
import EditProfilePhoto from "../../../profile-summary/edit-profile-photo/EditProfilePhoto";
import ContactSupport from "./ContactSupport";
export const SUCCESS_ANIMATION_TIMEOUT = 3000;
const ProfileDetails = ({
  isExpertApp = false
}) => {
  const {
    countryList
  } = useAppConfig();
  const {
    profile,
    saveProfile,
    isEditable
  } = useExpertProfileContext();
  const {
    addFailureNotification
  } = useNotificationContext();
  const {
    t
  } = useTranslation();
  const [isChangePassword, setIsChangePassword] = React.useState(false);
  const [firstName, setFirstName] = React.useState(INITIAL_FIELD);
  const [lastName, setLastName] = React.useState(INITIAL_FIELD);
  const [country, setCountry] = React.useState(findUserCountry(profile, countryList));
  const [gender, setGender] = React.useState({
    value: "",
    pristine: true
  });
  const [newPassword, setNewPassword] = React.useState(INITIAL_FIELD);
  const [currentPassword, setCurrentPassword] = React.useState(INITIAL_FIELD);
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [isCurrentPasswordNotCorrect, setIsCurrentPasswordNotCorrect] = React.useState(false);
  const [isProfileSuccess, setIsProfileSuccess] = React.useState(false);
  const [isPasswordSuccess, setIsPasswordSuccess] = React.useState(false);
  const [showPasswordInputTransition, setShowPasswordInputTransition] = React.useState(false);
  const [showButtonTransition, setShowButtonTransition] = React.useState(false);
  const GENDER_OPTIONS = React.useMemo(() => [{
    label: t("Male"),
    value: "male"
  }, {
    label: t("Female"),
    value: "female"
  }, {
    label: t("Other"),
    value: "other"
  }], []);
  React.useEffect(() => {
    if (profile) {
      setFirstName({
        value: profile.firstName || "",
        pristine: true
      });
      setLastName({
        value: profile.lastName || "",
        pristine: true
      });
      if (isExpertApp) {
        setGender({
          value: profile.gender,
          pristine: true
        });
      }
    }
  }, [profile]);
  React.useEffect(() => {
    if (countryList.length > 0) {
      setCountry(findUserCountry(profile, countryList));
    }
  }, [countryList]);
  const handleLastNameChange = e => {
    setLastName({
      value: e.target.value.trim() === "" ? e.target.value.trim() : e.target.value,
      pristine: false
    });
  };
  const handleFirstNameChange = e => {
    setFirstName({
      value: e.target.value.trim() === "" ? e.target.value.trim() : e.target.value,
      pristine: false
    });
  };
  const successAnimation = (setSuccess, isPassword = false) => {
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      setShowButtonTransition(true);
      if (isPassword) {
        setShowPasswordInputTransition(true);
      }
    }, SUCCESS_ANIMATION_TIMEOUT);
  };
  const onFormSubmit = () => {
    if (!firstName.pristine || !lastName.pristine || isExpertApp && !gender.pristine) {
      const profileFeilds = {
        firstName: {
          ...firstName,
          value: firstName.value.trim()
        },
        lastName: {
          ...lastName,
          value: lastName.value.trim()
        }
      };
      if (isExpertApp) {
        profileFeilds["gender"] = gender;
      }
      saveProfile(getAllDirtyFields(profileFeilds), newProfile => {
        if (newProfile && !isChangePassword) {
          successAnimation(setIsProfileSuccess);
        }
      }, "Update Profile Details");
    }
    if (isChangePassword) {
      changePassword(currentPassword.value, newPassword.value, error => {
        if (error) {
          let notification = "The current password is incorrect. Please try again";
          setIsCurrentPasswordNotCorrect(true);
          if (error.message !== "Incorrect username or password." && error.meaning) {
            notification = error.meaning;
          }
          if (error.message === "Attempt limit exceeded, please try after some time.") {
            notification = error.message;
            setIsCurrentPasswordNotCorrect(false);
            setIsChangePassword(false);
          }
          addFailureNotification(notification, "Update Profile Details");
        } else {
          successAnimation(setIsPasswordSuccess, true);
          setNewPassword(INITIAL_FIELD);
          setCurrentPassword(INITIAL_FIELD);
          setIsChangePassword(false);
        }
      });
    }
  };
  const renderPasswordEndAdornment = (iconButtonClick, flag) => /*#__PURE__*/React.createElement(IconButton, {
    onClick: iconButtonClick,
    edge: "end",
    size: "large"
  }, !flag ? /*#__PURE__*/React.createElement(VisibilityOff, {
    color: "action"
  }) : /*#__PURE__*/React.createElement(Visibility, {
    color: "action"
  }));
  const handleCurrentPasswordChange = e => {
    setCurrentPassword({
      value: e.target.value,
      pristine: false
    });
    if (isCurrentPasswordNotCorrect) {
      setIsCurrentPasswordNotCorrect(false);
    }
  };
  const handleRemovePhoto = () => {
    saveProfile({
      picture: "",
      thumbnail: ""
    }, () => null, "Update Profile Details");
  };
  const handleSavePhoto = (profileDetails, callback) => {
    saveProfile(profileDetails, () => {
      if (typeof callback === "function") {
        callback();
      }
    }, "Update Profile Details");
  };
  const {
    profileFieldErrors,
    isFormInvalid
  } = React.useMemo(() => {
    const fields = {
      firstName,
      lastName,
      currentPassword,
      newPassword
    };
    if (isExpertApp) {
      fields.gender = gender;
    }
    return validateProfileForm(fields, isChangePassword);
  }, [firstName, lastName, currentPassword, newPassword, isChangePassword, gender]);
  const selectedGender = React.useMemo(() => GENDER_OPTIONS.find(item => item.value === gender.value) || "", [gender]);
  const renderChangePasswordInput = React.useMemo(() => {
    return /*#__PURE__*/React.createElement(AnimationWrapper, {
      showAnimation: showPasswordInputTransition
    }, /*#__PURE__*/React.createElement(Grid, {
      mt: theme => theme.spacing(4),
      item: true,
      xs: 6
    }, /*#__PURE__*/React.createElement(TextField, {
      label: "Password",
      value: "*****************",
      disabled: true,
      endAdornment: /*#__PURE__*/React.createElement(Typography, {
        color: "primary",
        variant: "body",
        sx: {
          cursor: "pointer"
        },
        onClick: () => setIsChangePassword(true)
      }, "Change")
    })));
  }, [showPasswordInputTransition]);
  return /*#__PURE__*/React.createElement(AnimationWrapper, {
    showAnimation: true
  }, /*#__PURE__*/React.createElement(Grid, {
    sx: {
      maxWidth: "600px"
    }
  }, isEditable && /*#__PURE__*/React.createElement(ContactSupport, null), isExpertApp && /*#__PURE__*/React.createElement(Grid, {
    mt: theme => theme.spacing(4)
  }, /*#__PURE__*/React.createElement(EditProfilePhoto, {
    profile: profile,
    handleRemovePhoto: handleRemovePhoto,
    handleSavePhoto: handleSavePhoto
  })), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    mt: theme => isExpertApp ? theme.spacing(2) : theme.spacing(3),
    item: true
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "First Name",
    placeholder: "Type your first name",
    id: "firstNameProfileInputField",
    onChange: handleFirstNameChange,
    value: firstName.value,
    disabled: isEditable,
    error: profileFieldErrors.firstName ? true : false,
    errorText: profileFieldErrors.firstName
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Last Name",
    placeholder: "Type your last name",
    id: "lastNameProfileInputField",
    onChange: handleLastNameChange,
    value: lastName.value,
    disabled: isEditable,
    error: profileFieldErrors.lastName ? true : false,
    errorText: profileFieldErrors.lastName
  }))), /*#__PURE__*/React.createElement(Grid, {
    mt: theme => theme.spacing(3)
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Email",
    id: "emailProfileInputField",
    value: profile.email || "",
    disabled: true
  })), /*#__PURE__*/React.createElement(Grid, {
    mt: theme => theme.spacing(3),
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Country",
    id: "countryProfileInputField",
    value: country.value,
    disabled: true
  })), isExpertApp && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    mt: theme => theme.spacing(3),
    item: true
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Date of Birth",
    id: "dobProfileInputField",
    type: "date",
    value: getUserDob(profile).formattedValue,
    disabled: true,
    endAdornment: /*#__PURE__*/React.createElement(CalendarIcon, null)
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    pl: theme => theme.spacing(2)
  }, /*#__PURE__*/React.createElement(DropdownMenu, {
    label: "Gender",
    id: "genderProfileInputField",
    isOptionEqualToValue: (option, value) => option.value === value.value,
    value: selectedGender,
    disabled: isEditable,
    options: GENDER_OPTIONS,
    onChange: (e, selectedValue) => setGender({
      value: selectedValue.value,
      pristine: false
    }),
    placeholder: t("Select")
  }))), !isPasswordSuccess && /*#__PURE__*/React.createElement(React.Fragment, null, !isChangePassword ? renderChangePasswordInput : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    mt: theme => theme.spacing(4),
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Current Password",
    placeholder: "Type your current password",
    id: "currentPasswordProfileInputField",
    type: showCurrentPassword ? "text" : "password",
    onChange: handleCurrentPasswordChange,
    value: currentPassword.value,
    error: profileFieldErrors.currentPassword ? true : false || isCurrentPasswordNotCorrect,
    errorText: profileFieldErrors.currentPassword,
    required: true,
    endAdornment: renderPasswordEndAdornment(() => setShowCurrentPassword(!showCurrentPassword), showCurrentPassword)
  })), /*#__PURE__*/React.createElement(Grid, {
    mt: theme => theme.spacing(3),
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "New Password",
    placeholder: "Type your new password",
    id: "newPasswordInputField",
    type: showNewPassword ? "text" : "password",
    onChange: e => setNewPassword({
      value: e.target.value,
      pristine: false
    }),
    value: newPassword.value,
    error: profileFieldErrors.newPassword ? true : false,
    errorText: profileFieldErrors.newPassword,
    required: true,
    endAdornment: renderPasswordEndAdornment(() => setShowNewPassword(!showNewPassword), showNewPassword)
  })))), (isPasswordSuccess || isProfileSuccess) && /*#__PURE__*/React.createElement(SuccessAnimation, null), !isProfileSuccess && !isPasswordSuccess && /*#__PURE__*/React.createElement(Grid, {
    mt: theme => theme.spacing(3)
  }, /*#__PURE__*/React.createElement(AnimationWrapper, {
    showAnimation: showButtonTransition
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: isFormInvalid,
    onClick: onFormSubmit
  }, "Save Changes")))));
};
export default ProfileDetails;
ProfileDetails.propTypes = {
  isExpertApp: PropTypes.bool
};